<template>
  <wrapper-page>
    <template v-slot:MainContentHeaderActions>
      <div class="col-md-6 col-sm-12 text-end">
        <button
          :class="{ disabled: $v.$anyError }"
          class="btn btn-sm btn-primary"
          @click="saveDetails"
        >
          Create Client
        </button>
      </div>
    </template>

    <div class="row">
      <div class="col-12">
        <div class="page-title-box pb-3">
          <h4>New Client</h4>
        </div>
      </div>
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="form-check form-switch mb-3" style="padding-left: 0">
              <label class="form-check-label" for="client-type-switch"
                >Person</label
              >
              <input
                class="form-check-input float-none mx-2"
                type="checkbox"
                v-model="details.isCompany"
                id="client-type-switch"
              />
              <label class="form-check-label" for="client-type-switch"
                >Company</label
              >
            </div>

            <form @submit.prevent="saveDetails">
              <div class="row">
                <div
                  v-for="field in respectiveFields"
                  :key="field.name"
                  class="col-lg-3 col-md-6"
                >
                  <form-input-text
                    v-model="details[field.name]"
                    :error="$v.details[field.name].$error"
                    :error-message="validationMessage($v.details[field.name])"
                    :field="field"
                    @blur="field.name === 'name' ? onClientNameBlur() : null"
                  />
                </div>
                <template v-if="!details.isCompany">
                  <div
                    v-for="field in contactFields"
                    :key="`${0}-${field.name}`"
                    class="col-lg-3 col-md-6"
                  >
                    <form-input-text
                      v-model="$v.contacts.$each.$iter[0][field.name].$model"
                      :field="field"
                      @blur="onContactFieldBlur(0)"
                    />
                  </div>
                </template>
              </div>

              <!-- Suggested Client Matches -->
              <div
                v-if="clientSearchResults && clientSearchResults.length"
                class="alert alert-info border mt-3 mb-3 py-2 px-3 position-relative"
              >
                <div
                  class="d-flex align-items-center justify-content-between mb-2"
                >
                  <small class="fw-bold">
                    Matching client(s) found in database. Would you like to view
                    an existing client?
                  </small>
                  <button
                    type="button"
                    class="btn-close btn-close-sm"
                    @click="dismissClientSearchResults"
                  ></button>
                </div>

                <div class="list-group">
                  <div
                    v-for="match in clientSearchResults"
                    :key="match.id || match.client_id"
                    class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3"
                  >
                    <div>
                      <strong>{{ match.name || match.client_name }}</strong>
                      <span class="text-muted ms-2 fs-13">
                        <i v-if="match.email" class="ri-mail-line ms-2 me-1"></i
                        >{{ match.email }}
                      </span>
                    </div>
                    <button
                      type="button"
                      class="btn btn-xs btn-outline-primary"
                      @click="redirectToClient(match.id || match.client_id)"
                    >
                      View Client
                    </button>
                  </div>
                </div>
              </div>

              <address-editor
                v-for="(address, i) in addresses"
                :key="`address-${i}`"
                :address="address"
                class="col-lg-6"
              />
            </form>
          </div>
        </div>
      </div>
    </div>

    <div v-if="details.isCompany" class="row mt-4">
      <div class="col-12 d-flex justify-content-start align-items-center mb-3">
        <div class="page-title-box pb-0">
          <h4 class="mb-0">Contacts</h4>
        </div>
        <button
          class="ms-2 btn btn-sm btn-primary"
          title="Add Contact"
          type="button"
          @click="addContact"
        >
          <span class="sr-only">Add Contact</span>
          <i class="fa fa-plus"></i>
        </button>
      </div>
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <form @submit.prevent="saveDetails">
              <div
                v-for="(contact, i) in $v.contacts.$each.$iter"
                :key="`contact-${i}`"
                class="flex-column mb-3"
              >
                <div class="d-flex align-items-center">
                  <div class="row flex-grow-1 d-flex">
                    <div
                      v-for="field in contactFields"
                      :key="`${i}-${field.name}`"
                      class="col-lg-3 col-md-6"
                    >
                      <form-input-text
                        v-model="$v.contacts.$each.$iter[i][field.name].$model"
                        :error="$v.contacts.$each.$iter[i][field.name].$error"
                        :error-message="
                          validationMessage(
                            $v.contacts.$each.$iter[i][field.name]
                          )
                        "
                        :field="field"
                        @blur="onContactFieldBlur(i)"
                      />
                    </div>
                  </div>
                  <div
                    v-if="contacts.length !== 1"
                    class="ms-4 flex-shrink-0 d-flex"
                  >
                    <button
                      style="margin-top: 29px"
                      class="mb-auto btn btn-danger"
                      title="Remove Contact"
                      type="button"
                      @click="removeContact(i)"
                    >
                      <span class="sr-only">Remove Contact</span>
                      <i class="fa fa-minus"></i>
                    </button>
                  </div>
                </div>

                <!-- Suggested Contact Matches -->
                <div
                  v-if="
                    contactSearchResults[i] && contactSearchResults[i].length
                  "
                  class="alert alert-info border mt-2 mb-0 py-2 px-3 position-relative"
                >
                  <div
                    class="d-flex align-items-center justify-content-between mb-2"
                  >
                    <small class="fw-bold">
                      Matching contact(s) found in database. Would you like to
                      use an existing contact?
                    </small>
                    <button
                      type="button"
                      class="btn-close btn-close-sm"
                      @click="dismissSearchResults(i)"
                    ></button>
                  </div>

                  <div class="list-group">
                    <div
                      v-for="match in contactSearchResults[i]"
                      :key="match.id || match.contact_id"
                      class="list-group-item list-group-item-action d-flex justify-content-between align-items-center py-2 px-3"
                    >
                      <div>
                        <strong>{{ match.name || match.contact_name }}</strong>
                        <span class="text-muted ms-2 fs-13">
                          <i v-if="match.email" class="ri-mail-line me-1"></i
                          >{{ match.email }}
                          <i
                            v-if="match.phone || match.phone_number"
                            class="ri-phone-line ms-2 me-1"
                          ></i
                          >{{ match.phone || match.phone_number }}
                        </span>
                      </div>
                      <button
                        type="button"
                        class="btn btn-xs btn-outline-primary"
                        @click="selectExistingContact(i, match)"
                      >
                        Select Contact
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  </wrapper-page>
</template>

<script>
import WrapperPage from "../../components/layout/WrapperPage.vue";
import FormInputText from "../../components/ui/FormInputText.vue";
import { createClient } from "@/apis/clients";
import { searchClientsAndContacts } from "@/apis/search";
import { email, required } from "vuelidate/lib/validators";
import { notifyError, notifySuccess } from "@/helpers/notification";
import { validationMessage } from "@/helpers/validations";
import AddressEditor from "../../components/ui/AddressEditor.vue";
import { confirmRedirect } from "../../helpers/generic";
import { store } from "../../store/index";
import _ from "lodash";

export default {
  name: "ClientsCreate",
  components: {
    WrapperPage,
    FormInputText,
    AddressEditor,
  },
  data() {
    return {
      isSavingDetails: false,
      addresses: [{}],
      details: {
        isCompany: true,
      },
      contacts: [{ type: null, name: null, phone: null, email: null }],
      contactSearchResults: {},
      clientSearchResults: [],
      formChanged: false,
    };
  },
  validations: {
    details: {
      name: {
        required,
      },
      id_card: {},
      company_number: {},
      imo_number: {},
      vat_number: {},
      website: {},
    },
    contacts: {
      $each: {
        type: {},
        name: {
          required,
        },
        phone: {},
        email: {
          email,
        },
      },
    },
  },
  beforeRouteLeave(to, from, next) {
    if (this.formChanged) {
      confirmRedirect(next);
    } else {
      next();
    }
  },
  mounted() {
    store.commit("defaultStore/storeDefaultFormState", {
      addresses: this.addresses,
      details: this.details,
      contacts: this.contacts,
    });
  },
  methods: {
    validationMessage,
    addContact() {
      if (!this.details.isCompany && this.contacts.length >= 1) return;

      this.contacts.push({ type: null, name: null, phone: null, email: null });
    },
    removeContact(index) {
      if (this.contacts.length === 1) return;

      this.contacts.splice(index, 1);
      this.$delete(this.contactSearchResults, index);
    },
    onContactFieldBlur(index) {
      this.searchExistingContact(index);
    },
    onClientNameBlur() {
      this.searchExistingClient();
    },
    async searchExistingClient() {
      const clientName = this.details.name || "";

      // Split by whitespace and filter out unique words <= 3 characters
      const words = [
        ...new Set(
          clientName
            .split(/\s+/)
            .map((w) => w.trim())
            .filter((w) => w.length > 3)
        ),
      ];

      if (!words.length) {
        this.clientSearchResults = [];
        return;
      }

      try {
        // Parallel requests for all words > 3 characters
        const requests = words.map((word) =>
          searchClientsAndContacts({
            client_name: word,
            includeAll: true,
          })
        );
        const responses = await Promise.all(requests);
        const combinedResults = [];

        if (!responses || responses.length === 0) {
          this.clientSearchResults = [];
          return;
        }

        responses?.forEach((res) => {
          const results = res?.data || [];
          if (Array.isArray(results)) {
            combinedResults.push(...results);
          }
        });

        // Deduplicate results by client id
        const uniqueClientsMap = new Map();
        combinedResults.forEach((client) => {
          const id = client.id || client.client_id;
          if (id && !uniqueClientsMap.has(id)) {
            uniqueClientsMap.set(id, client);
          }
        });

        this.clientSearchResults = Array.from(uniqueClientsMap.values());
      } catch (err) {
        this.clientSearchResults = [];
      }
    },
    dismissClientSearchResults() {
      this.clientSearchResults = [];
    },
    redirectToClient(clientId) {
      if (!clientId) return;
      this.formChanged = false;
      this.$router.push({
        name: "clients-show",
        params: { clientId },
      });
    },
    async searchExistingContact(index) {
      const contact = this.contacts[index];

      // Only search for contacts without an assigned ID
      if (!contact || contact.id) return;

      const contactName = contact.name || "";
      const emailVal = contact.email || "";
      const phoneVal = contact.phone || "";

      if (!contactName.trim() && !emailVal.trim() && !phoneVal.trim()) {
        this.$set(this.contactSearchResults, index, []);
        return;
      }

      const query = {
        contact_name: contactName,
        email: emailVal,
        phone: phoneVal,
        includeAll: true,
      };

      try {
        const response = await searchClientsAndContacts(query);
        const results = response?.data?.contacts || response?.data || [];
        // Deduplicate results by client id
        const uniqueContactsMap = new Map();
        results?.forEach((contact) => {
          const id = contact.contact_id || "";
          if (id && !uniqueContactsMap.has(id)) {
            uniqueContactsMap.set(id, contact);
          }
        });
        this.$set(
          this.contactSearchResults,
          index,
          Array.from(uniqueContactsMap.values())
        );
      } catch (err) {
        this.$set(this.contactSearchResults, index, []);
      }
    },
    selectExistingContact(index, match) {
      const resolvedId = match.id || match.contact_id;

      this.$set(this.contacts, index, {
        ...this.contacts[index],
        id: resolvedId,
        name: match.name || match.contact_name || this.contacts[index].name,
        email: match.email || this.contacts[index].email,
        phone: match.phone || match.phone_number || this.contacts[index].phone,
        type: match.type || this.contacts[index].type,
      });

      this.$set(this.contactSearchResults, index, []);
    },
    dismissSearchResults(index) {
      this.$set(this.contactSearchResults, index, []);
    },
    async saveDetails() {
      if (
        !this.details.isCompany &&
        this.contacts.length &&
        !this.contacts[0].name
      ) {
        this.contacts[0].name = this.details.name;
      }

      this.$v.$touch();
      if (this.$v.$anyError) {
        notifyError(this, "not all required fields have been entered");
        return;
      }

      this.isSavingDetails = true;

      let client = { ...this.details };
      if (this.details.isCompany) {
        if (this.contacts && this.contacts.length && this.contacts[0].name)
          client.contacts = this.contacts;
      } else {
        const contactDetails = this.contacts[0];
        client.contacts = [
          {
            type: contactDetails.type,
            name: this.details.name,
            phone: contactDetails.phone,
            email: contactDetails.email,
            id: contactDetails.id,
          },
        ];
      }

      if (this.addresses && this.addresses.length && this.addresses[0].line1)
        client.addresses = this.addresses;

      await createClient(client)
        .then(async ({ data }) => {
          if (data.id) {
            notifySuccess(this, "Client has been created");
            this.$router.push({
              name: "clients-show",
              params: { clientId: data.id },
            });
          } else notifyError();
        })
        .catch((err) => {
          notifyError(this, err?.response?.data);
        });
      this.formChanged = false;
      this.isSavingDetails = false;
    },
    stateIsNotChanged() {
      const defaultDetails = store.state.defaultStore.defaultFormState?.details;
      const defaultAddresses =
        store.state.defaultStore.defaultFormState?.addresses;
      const defaultContacts =
        store.state.defaultStore.defaultFormState?.contacts;
      return (
        _.isEqual(this.details, defaultDetails) &&
        _.isEqual(this.addresses, defaultAddresses) &&
        _.isEqual(this.contacts, defaultContacts)
      );
    },
    updateFormChanged() {
      if (!this.formChanged) {
        this.formChanged = true;
        return;
      }
      if (this.stateIsNotChanged()) {
        this.formChanged = false;
      }
    },
  },
  computed: {
    contactFields() {
      const fields = [];

      if (this.details.isCompany)
        fields.push(
          { label: "Role", name: "type" },
          { label: "Name", name: "name" }
        );

      fields.push(
        { label: "Phone", name: "phone" },
        { label: "Email", name: "email", type: "email" }
      );

      return fields;
    },
    respectiveFields() {
      let fields = [];

      if (!this.details.isCompany) {
        fields = [
          { label: "Client Name", name: "name" },
          { label: "ID Card", name: "id_card" },
        ];
      }

      if (this.details.isCompany) {
        fields = [
          { label: "Company Name", name: "name" },
          { label: "Company Number", name: "company_number" },
          { label: "IMO Number", name: "imo_number" },
          { label: "VAT Number", name: "vat_number" },
          { label: "Website", name: "website" },
        ];
      }

      return fields;
    },
  },
  watch: {
    details: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
    addresses: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
    contacts: {
      handler() {
        this.updateFormChanged();
      },
      deep: true,
    },
  },
};
</script>
